import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/components/ThemeProvider";
import { useLanguage } from "@/components/LanguageProvider";
import { apiRequest } from "@/lib/queryClient";
import { fetchUserPreferences, setCachedUserPreferences } from "@/lib/userPreferences";
import type { LanguageCode } from "@shared/languageConfig";

/**
 * Invisible component that syncs user preferences (dark/light/system theme
 * and language) between the server and the local ThemeProvider/LanguageProvider.
 *
 * Mount this once inside the authenticated app shell. It:
 *   1. On first auth — fetches server-stored preferences and applies them only
 *      if the user has never explicitly chosen a theme on this device. Once they
 *      have, the server becomes write-only for theme: their local choice is
 *      sovereign and is never overridden no matter what page loads.
 *   2. When the user manually changes theme or language — saves the new value
 *      to the server without echoing it back on the same session.
 *
 * The `serverX` refs track the last value the server knows about so we never
 * save a value that the server itself just told us.
 */
export function UserPreferencesSync() {
  const { user } = useAuth();
  const { theme, themeIsExplicit, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();

  // null = not yet initialized from server; string = last-known server value.
  // This guards against echoing server-applied values back to the server.
  const serverTheme = useRef<string | null>(null);
  const serverLanguage = useRef<string | null>(null);

  // On first auth (or when user changes): fetch server prefs and apply them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;

    fetchUserPreferences()
      .then((prefs: Record<string, string>) => {
        const serverT = prefs.theme || theme;
        const serverL = prefs.language || language;

        // Language: server always wins (no login-screen language switcher).
        serverLanguage.current = serverL;
        if (serverL !== language) setLanguage(serverL as LanguageCode);

        if (themeIsExplicit) {
          // The user has already made a deliberate theme choice on this device
          // (at any point — login screen, settings, a previous session).
          // Their choice is sovereign: server must never override it.
          // If local differs from server, save local to server instead.
          serverTheme.current = theme;
          if (theme !== serverT) {
            setCachedUserPreferences({ theme });
            apiRequest("PATCH", "/api/users/me/preferences", { theme }).catch((err) => {
              console.error("[UserPreferencesSync] Failed to save theme preference:", err);
            });
          }
        } else {
          // Fresh device — user has never explicitly set a theme here.
          // Apply the server value (cross-device sync). Calling setTheme will
          // also mark themeIsExplicit=true so future syncs never override again.
          serverTheme.current = serverT;
          if (serverT !== theme) setTheme(serverT as "light" | "dark" | "system");
        }
      })
      .catch(() => {
        // On network failure, mark initialized using current local values so
        // subsequent user-initiated changes still get saved.
        serverTheme.current = theme;
        serverLanguage.current = language;
      });
    // Only re-run when the authenticated user changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Save theme to server when the user explicitly changes it.
  useEffect(() => {
    if (serverTheme.current === null) return; // not yet initialized
    if (theme === serverTheme.current) return; // no user change
    serverTheme.current = theme;
    setCachedUserPreferences({ theme });
    apiRequest("PATCH", "/api/users/me/preferences", { theme }).catch((err) => {
      console.error("[UserPreferencesSync] Failed to save theme preference:", err);
    });
  }, [theme]);

  // Save language to server when the user explicitly changes it.
  useEffect(() => {
    if (serverLanguage.current === null) return; // not yet initialized
    if (language === serverLanguage.current) return; // no user change
    serverLanguage.current = language;
    setCachedUserPreferences({ language });
    apiRequest("PATCH", "/api/users/me/preferences", { language }).catch((err) => {
      console.error("[UserPreferencesSync] Failed to save language preference:", err);
    });
  }, [language]);

  return null;
}
